// src/components/Radiologist/DycovueReportForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  IconButton,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Autocomplete,
  InputAdornment,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Save,
  Send,
  Close,
  CheckCircle,
  Visibility,
  Warning,
  LocalHospital,
  Search as SearchIcon,
  DeleteOutline as DeleteIcon,
  RateReview as PreliminaryIcon,
  Science as ScienceIcon,
  Flag,
  ChatBubbleOutline,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import {
  createDycovueReport,
  updateDycovueReport,
  approveDycovueReport,
  submitDycovuePreliminary,
  getDycovueReport,
  autoSaveReport,
  flagCriticalFinding,
  facilityReplyReport,
} from '../../services/reports';
import { getReportTemplate } from '../../services/reports';
import { getViewerUrl } from '../../services/studies';
import { procedureService } from '../../services/ris';
import { icdService } from '../../services/icd';
import { formatPatientName, formatDicomDate } from '../../services/utils';
import ReportTemplateForm from '../Common/ReportTemplateForm';
import { getPatientHistory } from '../../services/studies';
import { generateOHIFUrl } from '../../services/utils';
import RequestLabTestDialog from './RequestLabTestDialog';
import LabReportModal from './LabReportModal';
import { labReportService } from '../../services/lis';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { useAuth } from '../../contexts/AuthContext';

// Inline prior-studies panel rendered inside the report form tab
function PriorStudiesTab({ patientId, currentStudyId }) {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setError('');
    getPatientHistory(patientId)
      .then(data => setStudies(data.studies || []))
      .catch(() => setError('Failed to load prior studies.'))
      .finally(() => setLoading(false));
  }, [patientId]);

  const MODALITY_COLORS = {
    CT: '#2196F3', MR: '#9C27B0', US: '#00897B', XR: '#F57C00',
    CR: '#F57C00', DR: '#F57C00', MG: '#E91E63', PT: '#FF5722', OT: '#607D8B',
  };

  const openViewer = (s) => {
    const uid = s.studyInstanceUID || s.orthancStudyId;
    if (!uid) return;
    const base = process.env.REACT_APP_OHIF_URL || 'http://localhost:8042/ohif';
    const url = generateOHIFUrl(uid, base);
    const w = window.open(url, '_blank', 'noopener,noreferrer,width=1400,height=900');
    if (w) { w.opener = null; try { w.focus(); } catch (_) {} }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={28} /></Box>;
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  const priors = studies.filter(s => s._id !== currentStudyId);

  return (
    <Box>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        {priors.length} prior study record{priors.length !== 1 ? 's' : ''} for this patient
      </Typography>
      {priors.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>No prior imaging studies found for this patient.</Alert>
      )}
      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
        {priors.map(s => {
          const dateStr = s.studyDate
            ? new Date(s.studyDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
            : '—';
          const hasImages = s.totalInstances > 0 || s.instanceCount > 0;
          const color = MODALITY_COLORS[s.modality] || '#607D8B';
          return (
            <Paper key={s._id} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Chip
                  label={s.modality || 'OT'}
                  size="small"
                  sx={{ bgcolor: color, color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 22, mt: 0.3 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                      {s.studyDescription || 'Unnamed Study'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                      {dateStr}
                    </Typography>
                  </Box>
                  {s.accessionNumber && (
                    <Typography variant="caption" color="textSecondary">ACC: {s.accessionNumber}</Typography>
                  )}
                  {s.report?.impression && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.25 }}>IMPRESSION</Typography>
                      <Typography variant="caption" sx={{ lineHeight: 1.6 }}>{s.report.impression}</Typography>
                    </Box>
                  )}
                </Box>
                {hasImages && (
                  <Tooltip title="Open in viewer">
                    <IconButton size="small" onClick={() => openViewer(s)} sx={{ color: 'primary.main', mt: 0.25 }}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

const DycovueReportForm = ({ open, onClose, study, onSuccess }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const { user } = useAuth();
  const studyApiId = study?._id || study?.id || study?.studyInstanceUID;
  const [concernReplyText, setConcernReplyText] = useState('');
  const [sendingConcernReply, setSendingConcernReply] = useState(false);
  const [replySuccessMessage, setReplySuccessMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [reportData, setReportData] = useState({
    clinicalHistory: '',
    technique: '',
    comparison: '',
    findings: '',
    impression: '',
    recommendations: '',
    modalityFields: {},
    billingCodes: [],
    diagnosticCodes: []
  });
  const [procedureSearch, setProcedureSearch] = useState('');
  const [procedureResults, setProcedureResults] = useState([]);
  const [searchingProcedure, setSearchingProcedure] = useState(false);
  const [template, setTemplate] = useState(null);
  const [existingReport, setExistingReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reportId, setReportId] = useState(null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [hasCriticalFinding, setHasCriticalFinding] = useState(false);
  const [criticalDescription, setCriticalDescription] = useState('');
  const [viewerUrl, setViewerUrl] = useState('');
  const [labTestDialogOpen, setLabTestDialogOpen] = useState(false);
  const [labReport, setLabReport] = useState(null);
  const [labReportModalOpen, setLabReportModalOpen] = useState(false);

  // ICD-10 state
  const [icdSearch, setIcdSearch] = useState('');
  const [icdResults, setIcdResults] = useState([]);
  const [searchingIcd, setSearchingIcd] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  const steps = ['Review Study', 'Create Report', 'Review & Approve'];

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (open && study) {
        setLoading(true);
        try {
          // Load template
          const templateData = await getReportTemplate(study.modality);
          setTemplate(templateData);

          // Load existing report if exists
          try {
            const report = await getDycovueReport(studyApiId);
            if (report) {
              setExistingReport(report);
              setReportData({
                clinicalHistory: report.clinicalHistory || study.dycovueClinicalQuestion || '',
                technique: report.technique || '',
                comparison: report.comparison || '',
                findings: report.findings || '',
                impression: report.impression || '',
                recommendations: report.recommendations || '',
                modalityFields: report.modalityFields || {},
                billingCodes: report.billingCodes || [],
                diagnosticCodes: report.diagnosticCodes || []
              });
              setReportId(report._id);
              setHasCriticalFinding(report.criticalFindings?.present || false);
              setCriticalDescription(report.criticalFindings?.description || '');
            } else {
              // Pre-populate clinical history from request
              setReportData(prev => ({
                ...prev,
                clinicalHistory: study.dycovueClinicalQuestion || ''
              }));
            }
          } catch (err) {
            // No existing report, that's ok
            setReportData(prev => ({
              ...prev,
              clinicalHistory: study.dycovueClinicalQuestion || ''
            }));
          }

          // Get OHIF viewer URL - use correct format: /viewer/{studyInstanceUID}
          const ohifBaseUrl = process.env.REACT_APP_OHIF_URL || 'https://ohif-viewer.dycovue.com';
          
          // First try to use studyInstanceUID directly from the study object
          if (study?.studyInstanceUID) {
            const viewerUrlStr = generateOHIFUrl(study.studyInstanceUID, ohifBaseUrl);
            setViewerUrl(viewerUrlStr);
            console.log('OHIF viewer URL:', viewerUrlStr);
          } else {
            // Fallback: fetch studyInstanceUID from backend
            try {
              const urlData = await getViewerUrl(studyApiId);
              if (urlData?.studyInstanceUID) {
                const viewerUrlStr = generateOHIFUrl(urlData.studyInstanceUID, ohifBaseUrl);
                setViewerUrl(viewerUrlStr);
                console.log('OHIF viewer URL (from backend):', viewerUrlStr);
              } else if (urlData?.ohifUrl) {
                // Use backend-provided URL if studyInstanceUID not available
                setViewerUrl(urlData.ohifUrl);
                console.log('OHIF viewer URL (backend ohifUrl):', urlData.ohifUrl);
              }
            } catch (viewerErr) {
              console.warn('Could not get viewer URL:', viewerErr);
            }
          }

          // Check for completed lab reports
          try {
            if (study?.studyInstanceUID) {
              const labRes = await labReportService.getForStudy(study.studyInstanceUID);
              if (labRes.data?.data) {
                setLabReport(labRes.data.data);
              }
            } else if (study?.patientId) {
              const labRes = await labReportService.getForPatient(study.patientId);
              if (labRes.data?.data && labRes.data.data.length > 0) {
                setLabReport(labRes.data.data[0]);
              }
            }
          } catch (labErr) {
            console.debug('No lab report found for study:', labErr);
          }

        } catch (err) {
          console.error('Load data error:', err);
          setError('Failed to load report data');
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
  }, [open, study]);

  // Auto-save
  const triggerAutoSave = useCallback(() => {
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    const timeout = setTimeout(async () => {
      // Don't auto-save if report is approved and not flagged for revision
      if (existingReport && existingReport.status === 'approved' && existingReport.facilityReview?.status !== 'flagged') {
        return;
      }
      if (reportId && (reportData.findings || reportData.clinicalHistory || reportData.impression)) {
        try {
          setAutoSaveStatus('saving');
          await autoSaveReport(reportId, reportData);
          console.log('Auto-saved at', new Date().toLocaleTimeString());
          setAutoSaveStatus('saved');
          setTimeout(() => {
            setAutoSaveStatus(prev => (prev === 'saved' ? 'idle' : prev));
          }, 4000);
        } catch (err) {
          console.error('Auto-save failed:', err);
          setAutoSaveStatus('error');
        }
      }
    }, 3000);

    setAutoSaveTimeout(timeout);
  }, [reportId, reportData, autoSaveTimeout, existingReport]);

  useEffect(() => {
    if (reportId) {
      triggerAutoSave();
    }
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [reportData, reportId]);

  const handleFieldChange = (field, value) => {
    setReportData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleModalityFieldChange = (fieldName, value) => {
    setReportData(prev => ({
      ...prev,
      modalityFields: {
        ...prev.modalityFields,
        [fieldName]: value
      }
    }));
  };

  // Search the Procedure catalog for billing codes (debounced)
  useEffect(() => {
    if (!procedureSearch || procedureSearch.length < 2) {
      setProcedureResults([]);
      return;
    }
    setSearchingProcedure(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await procedureService.list({ q: procedureSearch });
        setProcedureResults((data.procedures || []).filter(p => p.cptCode));
      } catch (err) {
        console.error('Procedure search error:', err);
      } finally {
        setSearchingProcedure(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [procedureSearch]);

  const handleAddBillingCode = (procedure) => {
    if (!procedure?.cptCode) return;
    setReportData(prev => ({
      ...prev,
      billingCodes: [
        ...prev.billingCodes,
        { code: procedure.cptCode, description: procedure.description, rvu: procedure.rvu || 0 }
      ]
    }));
    setProcedureSearch('');
    setProcedureResults([]);
  };

  const handleRemoveBillingCode = (index) => {
    setReportData(prev => ({
      ...prev,
      billingCodes: prev.billingCodes.filter((_, i) => i !== index)
    }));
  };

  const handleBillingCodeFieldChange = (index, field, value) => {
    setReportData(prev => ({
      ...prev,
      billingCodes: prev.billingCodes.map((bc, i) => i === index ? { ...bc, [field]: value } : bc)
    }));
  };

  // Search ICD-10 diagnostic codes (debounced)
  useEffect(() => {
    if (!icdSearch) {
      const fetchCommon = async () => {
        try {
          const common = await icdService.getCommonIcdCodes();
          setIcdResults(common);
        } catch (err) {
          console.error(err);
        }
      };
      fetchCommon();
      return;
    }
    
    if (icdSearch.length < 2) return;

    setSearchingIcd(true);
    const timer = setTimeout(async () => {
      try {
        const results = await icdService.searchIcdCodes(icdSearch);
        setIcdResults(results || []);
      } catch (err) {
        console.error('ICD search error:', err);
      } finally {
        setSearchingIcd(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [icdSearch]);

  const handleAddIcdCode = (icd) => {
    if (!icd?.code) return;
    
    if (reportData.diagnosticCodes.some(c => c.code === icd.code)) {
      setIcdSearch('');
      return;
    }

    setReportData(prev => ({
      ...prev,
      diagnosticCodes: [
        ...prev.diagnosticCodes,
        { 
          code: icd.code, 
          description: icd.description, 
          isPrimary: prev.diagnosticCodes.length === 0
        }
      ]
    }));
    setIcdSearch('');
  };

  const handleRemoveIcdCode = (index) => {
    setReportData(prev => {
      const newCodes = prev.diagnosticCodes.filter((_, i) => i !== index);
      if (newCodes.length > 0 && !newCodes.some(c => c.isPrimary)) {
        newCodes[0].isPrimary = true;
      }
      return { ...prev, diagnosticCodes: newCodes };
    });
  };

  const handleSetPrimaryIcdCode = (index) => {
    setReportData(prev => ({
      ...prev,
      diagnosticCodes: prev.diagnosticCodes.map((c, i) => ({
        ...c,
        isPrimary: i === index
      }))
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return true; // Study review
      case 1:
        if (!reportData.clinicalHistory?.trim()) {
          setError('Clinical history is required');
          return false;
        }
        if (!reportData.technique?.trim()) {
          setError('Technique is required');
          return false;
        }
        if (!reportData.findings?.trim()) {
          setError('Findings are required');
          return false;
        }
        if (!reportData.impression?.trim()) {
          setError('Impression is required');
          return false;
        }
        if (hasCriticalFinding && !criticalDescription.trim()) {
          setError('Critical finding description is required');
          return false;
        }
        return true;
      case 2:
        return true; // Review
      default:
        return true;
    }
  };

  const handleNext = () => {
    setError('');
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setError('');

    try {
      let currentReportId = reportId;
      
      if (reportId) {
        // Update existing report
        try {
          await updateDycovueReport(reportId, reportData);
        } catch (updateErr) {
          // Handle case where report is not in draft status
          if (updateErr.response?.status === 400) {
            const errorMsg = updateErr.response?.data?.error || '';
            if (errorMsg.includes('Can only update draft reports')) {
              setError('Cannot update report: Report is no longer in draft status.');
              setSaving(false);
              return;
            }
          }
          throw updateErr;
        }
      } else {
        // Create new report - provide draft fallbacks for any empty required sections
        const draftData = {
          ...reportData,
          clinicalHistory: reportData.clinicalHistory?.trim() || 'Draft - In progress',
          technique: reportData.technique?.trim() || 'Draft - In progress',
          findings: reportData.findings?.trim() || 'Draft - In progress',
          impression: reportData.impression?.trim() || 'Draft - In progress',
        };

        try {
          const result = await createDycovueReport(studyApiId, draftData);
          currentReportId = result._id;
          setReportId(result._id);
          setExistingReport(result);

          // If user had specific field data, update to ensure exact user state is preserved
          const hasRealData = reportData.clinicalHistory || reportData.technique || reportData.findings || reportData.impression;
          if (hasRealData) {
            await updateDycovueReport(result._id, reportData);
          }
        } catch (createErr) {
          // Handle conflict - report already exists
          if (createErr.response?.status === 409) {
            const existingReportId = createErr.response?.data?.reportId;
            if (existingReportId) {
              currentReportId = existingReportId;
              setReportId(existingReportId);
              // Try to update the existing report
              await updateDycovueReport(existingReportId, reportData);
            } else {
              throw new Error('Report already exists but reportId not provided');
            }
          } else {
            throw createErr;
          }
        }
      }

      // Flag critical finding if needed - use currentReportId, not the stale reportId state
      if (hasCriticalFinding && criticalDescription.trim() && currentReportId) {
        try {
          await flagCriticalFinding(currentReportId, criticalDescription);
        } catch (flagErr) {
          console.error('Failed to flag critical finding:', flagErr);
          // Don't fail the whole save if flagging fails
        }
      }

      // Auto-send concern reply if entered
      if (concernReplyText.trim() && currentReportId) {
        try {
          const replyRes = await facilityReplyReport(currentReportId, concernReplyText.trim());
          if (replyRes?.report) {
            setExistingReport(replyRes.report);
          }
          setConcernReplyText('');
          setReplySuccessMessage('Your reply was saved and sent to the hospital facility.');
        } catch (replyErr) {
          console.error('Failed to auto-send reply to hospital concerns:', replyErr);
        }
      }

      onSuccess?.();
    } catch (err) {
      console.error('Save draft error:', err);
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.errors?.[0]?.msg || 
                          err.message ||
                          'Failed to save draft';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!validateStep(1)) {
      setActiveStep(1); // Go back to report step
      return;
    }

    setSaving(true);
    setError('');

    try {
      let finalReportId = reportId;

      // Create or update report first
      if (!reportId) {
        try {
          const result = await createDycovueReport(studyApiId, reportData);
          finalReportId = result._id;
          setReportId(finalReportId);
        } catch (createErr) {
          // Handle conflict - report already exists
          if (createErr.response?.status === 409) {
            const existingReportId = createErr.response?.data?.reportId;
            if (existingReportId) {
              finalReportId = existingReportId;
              setReportId(finalReportId);
              // Try to update the existing report before approving
              try {
                await updateDycovueReport(existingReportId, reportData);
              } catch (updateErr) {
                // If update fails, still proceed to approval
                console.log('Could not update existing report, proceeding to approval');
              }
            } else {
              throw new Error('Report already exists but reportId not provided');
            }
          } else {
            throw createErr;
          }
        }
      } else {
        // Try to update, but if report is not in draft status, skip update and go straight to approval
        try {
          await updateDycovueReport(reportId, reportData);
        } catch (updateErr) {
          const errorMsg = updateErr.response?.data?.error || '';
          if (updateErr.response?.status === 400 && errorMsg.includes('Can only update draft reports')) {
            console.log('Report not in draft status, skipping update and proceeding to approval');
          } else {
            throw updateErr;
          }
        }
      }

      // Flag critical finding if needed
      if (hasCriticalFinding && criticalDescription.trim() && finalReportId) {
        try {
          await flagCriticalFinding(finalReportId, criticalDescription);
        } catch (flagErr) {
          console.error('Failed to flag critical finding:', flagErr);
          // Don't fail the approval if flagging fails
        }
      }

      // Auto-send concern reply if entered
      if (concernReplyText.trim() && finalReportId) {
        try {
          await facilityReplyReport(finalReportId, concernReplyText.trim());
          setConcernReplyText('');
        } catch (replyErr) {
          console.error('Failed to auto-send reply to hospital concerns during approval:', replyErr);
        }
      }

      // Approve report
      await approveDycovueReport(finalReportId);

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve report');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitPreliminary = async () => {
    if (!validateStep(1)) {
      setActiveStep(1);
      return;
    }

    setSaving(true);
    setError('');

    try {
      let finalReportId = reportId;

      if (!finalReportId) {
        const result = await createDycovueReport(studyApiId, reportData);
        finalReportId = result._id;
        setReportId(finalReportId);
      } else {
        try {
          await updateDycovueReport(finalReportId, reportData);
        } catch (updateErr) {
          const errorMsg = updateErr.response?.data?.error || '';
          if (!(updateErr.response?.status === 400 && errorMsg.includes('Can only update draft'))) {
            throw updateErr;
          }
        }
      }

      // Auto-send concern reply if entered
      if (concernReplyText.trim() && finalReportId) {
        try {
          await facilityReplyReport(finalReportId, concernReplyText.trim());
          setConcernReplyText('');
        } catch (replyErr) {
          console.error('Failed to auto-send reply to hospital concerns during preliminary submission:', replyErr);
        }
      }

      await submitDycovuePreliminary(finalReportId);

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit preliminary report');
    } finally {
      setSaving(false);
    }
  };

  const handleSendConcernReply = async () => {
    const replyText = concernReplyText.trim();
    if (!replyText) return;

    const targetReportId = existingReport?._id || reportId;
    if (!targetReportId) {
      setError('Report ID is missing to post reply. Please save as draft first.');
      return;
    }

    setSendingConcernReply(true);
    setReplySuccessMessage('');
    try {
      const res = await facilityReplyReport(targetReportId, replyText);
      const updatedReport = res?.report || res?.data || res;
      if (updatedReport?._id) {
        setExistingReport(updatedReport);
      } else {
        setExistingReport((prev) => {
          const prevReview = prev?.facilityReview || {};
          const fallbackReplies = [
            ...(prevReview.replies || []),
            {
              user: user ? { firstName: user.firstName, lastName: user.lastName } : null,
              message: replyText,
              createdAt: new Date().toISOString(),
            },
          ];
          return {
            ...prev,
            facilityReview: {
              ...prevReview,
              radiologistReply: replyText,
              repliedAt: new Date().toISOString(),
              replies: fallbackReplies,
            },
          };
        });
      }

      setConcernReplyText('');
      setReplySuccessMessage('Your response has been sent to the hospital facility.');
    } catch (err) {
      console.error('Failed to post reply to flagged report:', err);
      setError(err.response?.data?.error || 'Failed to post reply to hospital concerns');
    } finally {
      setSendingConcernReply(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setActiveStep(0);
      setActiveTab(0);
      setReportData({
        clinicalHistory: '',
        technique: '',
        comparison: '',
        findings: '',
        impression: '',
        recommendations: '',
        modalityFields: {},
        billingCodes: []
      });
      setProcedureSearch('');
      setProcedureResults([]);
      setError('');
      setReportId(null);
      setExistingReport(null);
      setHasCriticalFinding(false);
      setCriticalDescription('');
      setConcernReplyText('');
      setSendingConcernReply(false);
      setReplySuccessMessage('');
      onClose();
    }
  };

  const handleOpenViewer = () => {
    // If we have a viewerUrl, use it directly
    if (viewerUrl) {
      window.open(viewerUrl, '_blank');
      return;
    }
    
    // Fallback: construct viewer URL from study data
    // Correct OHIF URL format: /viewer/{studyInstanceUID}
    const ohifBaseUrl = process.env.REACT_APP_OHIF_URL || 'https://ohif-viewer.dycovue.com';
    if (study?.studyInstanceUID) {
      const url = generateOHIFUrl(study.studyInstanceUID, ohifBaseUrl);
      console.log('Opening OHIF viewer (fallback):', url);
      window.open(url, '_blank');
    } else {
      console.error('Cannot open viewer: No studyInstanceUID available');
      setError('Cannot open viewer: Study UID not available');
    }
  };

  const renderFlaggedConcernsWithReply = () => {
    const isFlagged = existingReport?.facilityReview?.status === 'flagged';
    const concerns = existingReport?.facilityReview?.concerns;
    if (!isFlagged && !concerns) return null;

    const review = existingReport?.facilityReview || {};
    const replies = review.replies || [];
    const hasReplies = replies.length > 0 || Boolean(review.radiologistReply);

    return (
      <Paper
        variant="outlined"
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: '10px',
          bgcolor: isDark ? 'rgba(239, 68, 68, 0.08)' : '#FFF7ED',
          borderColor: isDark ? alpha('#F97316', 0.4) : '#FDBA74',
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25} flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Flag sx={{ color: '#EA580C', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: isDark ? '#FED7AA' : '#9A3412' }}>
              Hospital Flagged Concerns &amp; Clarifications
            </Typography>
          </Box>
          <Chip
            size="small"
            label="Facility Concerns Raised"
            sx={{
              bgcolor: isDark ? 'rgba(234, 88, 12, 0.25)' : '#FFEDD5',
              color: isDark ? '#FDBA74' : '#C2410C',
              fontWeight: 700,
              fontSize: '0.72rem',
              border: '1px solid',
              borderColor: isDark ? '#EA580C' : '#FDBA74',
            }}
          />
        </Box>

        {/* Hospital Concerns Content */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: '8px',
            bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
            borderColor: isDark ? alpha('#F97316', 0.3) : '#FED7AA',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#FDBA74' : '#C2410C' }}>
              Hospital Feedback / Reason for Flag:
            </Typography>
            {review.flaggedAt && (
              <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.7rem' }}>
                {new Date(review.flaggedAt).toLocaleString()}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: isDark ? '#F1F5F9' : '#1F2937', fontWeight: 500 }}>
            {concerns || 'No details provided.'}
          </Typography>
        </Paper>

        {/* Prior Radiologist Replies (Thread) */}
        {hasReplies && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: isDark ? '#93C5FD' : '#1D4ED8',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 0.75,
              }}
            >
              <ChatBubbleOutline sx={{ fontSize: 14 }} />
              Radiologist Responses to Hospital:
            </Typography>
            <Stack spacing={1}>
              {replies.length > 0 ? (
                replies.map((rep, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: '8px',
                      bgcolor: isDark ? 'rgba(37, 99, 235, 0.12)' : '#EFF6FF',
                      borderColor: isDark ? alpha('#3B82F6', 0.35) : alpha('#3B82F6', 0.25),
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#BFDBFE' : '#1E40AF' }}>
                        {rep.user?.firstName ? `Dr. ${rep.user.firstName} ${rep.user.lastName || ''}` : 'In-House Radiologist'}
                      </Typography>
                      {rep.createdAt && (
                        <Typography variant="caption" sx={{ color: isDark ? '#93C5FD' : '#60A5FA', fontSize: '0.68rem' }}>
                          {new Date(rep.createdAt).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: isDark ? '#F1F5F9' : '#1F2937', fontSize: '0.82rem' }}>
                      {rep.message}
                    </Typography>
                  </Paper>
                ))
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    borderRadius: '8px',
                    bgcolor: isDark ? 'rgba(37, 99, 235, 0.12)' : '#EFF6FF',
                    borderColor: isDark ? alpha('#3B82F6', 0.35) : alpha('#3B82F6', 0.25),
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#BFDBFE' : '#1E40AF' }}>
                      {review.repliedBy?.firstName
                        ? `Dr. ${review.repliedBy.firstName} ${review.repliedBy.lastName || ''}`
                        : 'In-House Radiologist'}
                    </Typography>
                    {review.repliedAt && (
                      <Typography variant="caption" sx={{ color: isDark ? '#93C5FD' : '#60A5FA', fontSize: '0.68rem' }}>
                        {new Date(review.repliedAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: isDark ? '#F1F5F9' : '#1F2937', fontSize: '0.82rem' }}>
                    {review.radiologistReply}
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Box>
        )}

        {/* Success message banner if just sent */}
        {replySuccessMessage && (
          <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="caption" fontWeight={600}>
              {replySuccessMessage}
            </Typography>
          </Alert>
        )}

        {/* Reply Composer */}
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            placeholder="Type your reply / clinical clarification to the hospital facility..."
            value={concernReplyText}
            onChange={(e) => setConcernReplyText(e.target.value)}
            disabled={sendingConcernReply}
            sx={{
              bgcolor: isDark ? '#141414' : '#FFFFFF',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                fontSize: '0.85rem',
              },
            }}
          />
          <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} gap={1}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSendConcernReply}
              disabled={sendingConcernReply || !concernReplyText.trim()}
              startIcon={sendingConcernReply ? <CircularProgress size={16} color="inherit" /> : <Send sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#EA580C',
                '&:hover': { bgcolor: '#C2410C' },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: '6px',
                px: 2,
              }}
            >
              {sendingConcernReply ? 'Sending Reply...' : 'Send Reply to Hospital'}
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Study Information & Clinical Context
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Review the study details and clinical question before creating your report.
            </Typography>

            {/* Priority Alert */}
            {study?.dycovuePriority === 'stat' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  STAT (within 1 hr) - Report required within 1 hour
                </Typography>
              </Alert>
            )}

            {/* Hospital Flagged Concerns & Radiologist Reply Thread */}
            {renderFlaggedConcernsWithReply()}

            {/* Study Info */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                Patient Information
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Patient Name:</Typography>
                  <Typography variant="body2">{formatPatientName(study?.patientName || study?.patient?.patientName)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Patient ID:</Typography>
                  <Typography variant="body2">{study?.patientId || study?.patient?.patientId || '—'}</Typography>
                </Box>
                {(study?.patientSex || study?.patient?.patientSex) && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">Sex:</Typography>
                    <Typography variant="body2">{study?.patientSex || study?.patient?.patientSex}</Typography>
                  </Box>
                )}
                {(study?.patientBirthDate || study?.patient?.patientBirthDate) && (
                  <Box>
                    <Typography variant="caption" color="textSecondary">DOB:</Typography>
                    <Typography variant="body2">
                      {formatDicomDate(study?.patientBirthDate || study?.patient?.patientBirthDate)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                Study Details
              </Typography>
              <Stack spacing={1}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Modality:</Typography>
                  <Typography variant="body2">{study?.modality || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Study Date:</Typography>
                  <Typography variant="body2">
                    {study?.studyDate ? new Date(study.studyDate).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Description:</Typography>
                  <Typography variant="body2">{study?.studyDescription || 'Diagnostic Scan'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Images:</Typography>
                  <Typography variant="body2">
                    {study?.totalInstances ?? study?.instanceCount ?? 0} instances in {study?.totalSeries ?? study?.seriesCount ?? 1} series
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Clinical Question */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Clinical Question from Referring Physician
              </Typography>
              <Typography variant="body2">
                {study?.dycovueClinicalQuestion || 'No specific clinical question provided'}
              </Typography>
              {study?.dycovueRequestedBy && (
                <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                  Requested by: {study.dycovueRequestedBy.firstName} {study.dycovueRequestedBy.lastName}
                </Typography>
              )}
            </Paper>

            {/* Viewer Button */}
            <Button
              fullWidth
              variant="contained"
              startIcon={<Visibility />}
              onClick={handleOpenViewer}
              disabled={!viewerUrl && !study?.studyInstanceUID}
              size="large"
            >
              Open Image Viewer (OHIF)
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" mb={2}>
              Dycovue Report (IHE MRRT Standard)
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
              <Tab label="Report Content" />
              <Tab label="Prior Studies" />
              <Tab label="Critical Findings" />
            </Tabs>

            {activeTab === 0 && (
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Embedded Image Viewer */}
                {viewerUrl && (
                  <Box
                    sx={{
                      flexBasis: { md: '45%' },
                      flexGrow: 1,
                      height: 380,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.default'
                    }}
                  >
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontSize: '0.8rem' }}>
                        Image Viewer
                      </Typography>
                    </Box>
                    <Box sx={{ height: 'calc(100% - 35px)' }}>
                      <iframe
                        src={viewerUrl}
                        title="OHIF Viewer"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                      />
                    </Box>
                  </Box>
                )}

                {/* Report Form */}
                <Box sx={{ flexBasis: { md: viewerUrl ? '55%' : '100%' }, flexGrow: 1 }}>
                  <Grid container spacing={1.2}>
                    {/* Clinical History & Technique side-by-side */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        rows={2}
                        size="small"
                        label="Clinical History / Indication"
                        placeholder="Clinical presentation and reason..."
                        value={reportData.clinicalHistory}
                        onChange={(e) => handleFieldChange('clinicalHistory', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        rows={2}
                        size="small"
                        label="Technique"
                        placeholder="Protocol, contrast, parameters..."
                        value={reportData.technique}
                        onChange={(e) => handleFieldChange('technique', e.target.value)}
                      />
                    </Grid>

                    {/* Comparison & Recommendations side-by-side */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        label="Comparison"
                        placeholder="Prior studies available..."
                        value={reportData.comparison}
                        onChange={(e) => handleFieldChange('comparison', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        label="Recommendations"
                        placeholder="Follow-up, next steps..."
                        value={reportData.recommendations}
                        onChange={(e) => handleFieldChange('recommendations', e.target.value)}
                      />
                    </Grid>

                    {/* Modality-Specific Fields */}
                    {template && template.customFields && template.customFields.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                          {study.modality} Specific Measurements & Findings
                        </Typography>
                        <ReportTemplateForm
                          template={template}
                          values={reportData.modalityFields}
                          onChange={handleModalityFieldChange}
                        />
                      </Grid>
                    )}

                    {/* Findings */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        rows={3.5}
                        size="small"
                        label="Findings"
                        placeholder="Systematic description of findings by anatomical region or organ system..."
                        value={reportData.findings}
                        onChange={(e) => handleFieldChange('findings', e.target.value)}
                      />
                    </Grid>

                    {/* Impression */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        multiline
                        rows={2.5}
                        size="small"
                        label="Impression / Conclusion"
                        placeholder="1. Summary of key findings\n2. Clinical significance..."
                        value={reportData.impression}
                        onChange={(e) => handleFieldChange('impression', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            {activeTab === 1 && (
              <PriorStudiesTab patientId={study?.patientId} currentStudyId={study?._id} />
            )}

            {activeTab === 2 && (
              <Box>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Critical Finding Notification
                  </Typography>
                  <Typography variant="body2">
                    If this study contains critical or urgent findings that require immediate clinical attention,
                    flag them here. The referring physician will be immediately notified.
                  </Typography>
                </Alert>

                <FormControlLabel
                  control={
                    <Switch
                      checked={hasCriticalFinding}
                      onChange={(e) => setHasCriticalFinding(e.target.checked)}
                      color="error"
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight="medium">
                      This study contains CRITICAL FINDINGS requiring immediate attention
                    </Typography>
                  }
                />

                {hasCriticalFinding && (
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Critical Finding Description"
                    placeholder="Describe the critical finding and its clinical urgency..."
                    value={criticalDescription}
                    onChange={(e) => setCriticalDescription(e.target.value)}
                    error={hasCriticalFinding && !criticalDescription.trim()}
                    helperText="This will trigger immediate notification to the referring physician"
                    sx={{ mt: 2 }}
                  />
                )}

                {hasCriticalFinding && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Examples of critical findings: acute intracranial hemorrhage, pneumothorax, pulmonary embolism,
                      aortic dissection, bowel perforation, etc.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review & Approve Report
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Please review your report carefully. Once approved, it will be finalized and sent to the referring physician.
            </Typography>

            {hasCriticalFinding && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  ⚠️ CRITICAL FINDING - Immediate notification will be sent
                </Typography>
                <Typography variant="body2">
                  {criticalDescription}
                </Typography>
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2.5, maxHeight: '45vh', overflowY: 'auto' }}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Clinical History
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {reportData.clinicalHistory}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Technique
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {reportData.technique}
                  </Typography>
                </Box>

                {reportData.comparison && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Comparison
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {reportData.comparison}
                      </Typography>
                    </Box>
                  </>
                )}

                <Divider />

                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Findings
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {reportData.findings}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Impression
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {reportData.impression}
                  </Typography>
                </Box>

                {reportData.recommendations && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Recommendations
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {reportData.recommendations}
                      </Typography>
                    </Box>
                  </>
                )}

                <Divider />

                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Billing Codes
                  </Typography>

                  {reportData.billingCodes.length > 0 && (
                    <Stack spacing={1} sx={{ mb: 1.5 }}>
                      {reportData.billingCodes.map((bc, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1}>
                          <TextField
                            size="small" label="Code" value={bc.code}
                            sx={{ width: 110 }}
                            onChange={(e) => handleBillingCodeFieldChange(index, 'code', e.target.value)}
                          />
                          <TextField
                            size="small" label="Description" value={bc.description} fullWidth
                            onChange={(e) => handleBillingCodeFieldChange(index, 'description', e.target.value)}
                          />
                          <TextField
                            size="small" label="RVU" type="number" value={bc.rvu}
                            sx={{ width: 90 }}
                            inputProps={{ step: 0.1, min: 0 }}
                            onChange={(e) => handleBillingCodeFieldChange(index, 'rvu', parseFloat(e.target.value) || 0)}
                          />
                          <IconButton size="small" color="error" onClick={() => handleRemoveBillingCode(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  <Autocomplete
                    options={procedureResults}
                    getOptionLabel={(p) => `${p.cptCode} — ${p.description}`}
                    inputValue={procedureSearch}
                    onInputChange={(_, v) => setProcedureSearch(v)}
                    onChange={(_, p) => p && handleAddBillingCode(p)}
                    loading={searchingProcedure}
                    noOptionsText={procedureSearch.length < 2 ? 'Type to search the procedure catalog' : 'No matching procedures with a CPT code'}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search procedure catalog to add a billing code"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {searchingProcedure && <CircularProgress size={16} />}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Box>
                
                <Divider />

                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    ICD-10 Diagnostic Codes
                  </Typography>

                  {reportData.diagnosticCodes && reportData.diagnosticCodes.length > 0 && (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {reportData.diagnosticCodes.map((icd, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={1}>
                          <Chip 
                            label={icd.isPrimary ? "PRIMARY" : "SECONDARY"} 
                            color={icd.isPrimary ? "primary" : "default"} 
                            size="small" 
                            onClick={() => handleSetPrimaryIcdCode(index)}
                            sx={{ cursor: 'pointer', minWidth: 90 }}
                          />
                          <TextField
                            size="small" label="ICD-10 Code" value={icd.code}
                            InputProps={{ readOnly: true }}
                            sx={{ width: 130 }}
                          />
                          <TextField
                            size="small" label="Description" value={icd.description} fullWidth
                            InputProps={{ readOnly: true }}
                          />
                          <IconButton size="small" color="error" onClick={() => handleRemoveIcdCode(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  <Autocomplete
                    options={icdResults}
                    getOptionLabel={(option) => `${option.code} — ${option.description}`}
                    inputValue={icdSearch}
                    onInputChange={(_, v) => setIcdSearch(v)}
                    onChange={(_, icd) => icd && handleAddIcdCode(icd)}
                    loading={searchingIcd}
                    filterOptions={(x) => x} // Disable built-in filtering, backend handles it
                    noOptionsText={icdSearch.length < 2 ? 'Type to search ICD-10 codes' : 'No matching ICD-10 codes'}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {option.code}
                            {option.isCommon && <Chip size="small" label="Common" color="primary" sx={{ ml: 1, height: 16, fontSize: '0.65rem' }} />}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search ICD-10 diagnostic codes (e.g. Chest pain)"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {searchingIcd && <CircularProgress size={16} />}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Paper>

            {existingReport?.status === 'qa_rejected' && existingReport?.qaComments && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  QA Review Feedback
                </Typography>
                <Typography variant="body2">{existingReport.qaComments}</Typography>
              </Alert>
            )}

            {/* Hospital Flagged Concerns & Radiologist Reply Thread */}
            {renderFlaggedConcernsWithReply()}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Submit as Preliminary</strong> — makes the report visible to clinicians with a PRELIMINARY watermark while awaiting QA approval.<br />
                <strong>Approve &amp; Submit Final</strong> — directly finalizes the report, generates a PDF, and notifies the referring physician.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      disableEscapeKeyDown={saving}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '92vw', md: '920px', lg: '980px' },
          maxWidth: '1000px !important',
          maxHeight: '88vh',
          borderRadius: '14px',
        }
      }}
    >
      <DialogTitle sx={{ px: 2.5, py: 1.25 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <LocalHospital color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
              Dycovue Report - {formatPatientName(study?.patientName || study?.patient?.patientName)}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={saving} size="small">
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderStepContent(activeStep)}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.25, justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={1}>
          {reportId && (
            <Chip 
              label={
                autoSaveStatus === 'saving'
                  ? 'Auto-saving...'
                  : autoSaveStatus === 'saved'
                    ? 'Draft saved'
                    : autoSaveStatus === 'error'
                      ? 'Auto-save failed'
                      : 'Draft'
              } 
              size="small" 
              color={
                autoSaveStatus === 'error'
                  ? 'error'
                  : autoSaveStatus === 'saved'
                    ? 'success'
                    : autoSaveStatus === 'saving'
                      ? 'warning'
                      : 'default'
              } 
              variant="outlined"
              sx={{
                opacity: autoSaveStatus === 'idle' ? 0.6 : 1,
                fontSize: '0.72rem',
                height: 24,
                fontWeight: 600,
                transition: 'all 0.25s ease'
              }}
            />
          )}
          {labReport && (
            <Tooltip title="View completed laboratory diagnostic results for this patient">
              <Button
                size="small"
                onClick={() => setLabReportModalOpen(true)}
                startIcon={<ScienceIcon />}
                sx={{
                  textTransform: 'none',
                  color: '#16A34A',
                  borderColor: '#16A34A',
                  bgcolor: 'rgba(22, 163, 74, 0.06)',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#15803D', bgcolor: 'rgba(22, 163, 74, 0.12)' }
                }}
                variant="outlined"
              >
                View Lab Results
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Request a laboratory test for this patient">
            <Button
              size="small"
              onClick={() => setLabTestDialogOpen(true)}
              startIcon={<ScienceIcon />}
              sx={{
                textTransform: 'none',
                color: '#7C3AED',
                borderColor: '#7C3AED',
                '&:hover': { borderColor: '#6D28D9', bgcolor: 'rgba(124, 58, 237, 0.04)' }
              }}
              variant="outlined"
            >
              Request Lab Test
            </Button>
          </Tooltip>
        </Box>

        <Box display="flex" gap={1}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>

          {activeStep > 0 && (
            <Button onClick={handleBack} disabled={saving}>
              Back
            </Button>
          )}

          {activeStep < steps.length - 1 && (
            <>
              <Button
                onClick={handleSaveDraft}
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                disabled={saving}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={saving}
              >
                Next
              </Button>
            </>
          )}

          {activeStep === steps.length - 1 && (
            <>
              <Button
                onClick={handleSaveDraft}
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                disabled={saving}
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                onClick={handleApprove}
                startIcon={saving ? <CircularProgress size={20} /> : <CheckCircle />}
                disabled={saving}
                color="success"
              >
                Approve & Submit Final
              </Button>
            </>
          )}
        </Box>
      </DialogActions>

      {/* Request Lab Test Dialog */}
      <RequestLabTestDialog
        open={labTestDialogOpen}
        onClose={() => setLabTestDialogOpen(false)}
        patientId={study?.patientId}
        patientName={study ? formatPatientName(study.patientName) : ''}
        studyUID={study?.studyInstanceUID}
        isDark={mode === 'dark'}
      />

      {/* View Lab Findings Modal */}
      <LabReportModal
        open={labReportModalOpen}
        onClose={() => setLabReportModalOpen(false)}
        resultId={labReport?.labResult || labReport?._id}
        patientName={study ? formatPatientName(study.patientName) : ''}
        isDark={mode === 'dark'}
      />
    </Dialog>
  );
};

export default DycovueReportForm;
